import { createId } from '@paralleldrive/cuid2';
import type { Chat } from '@trinity/chat';
import { KolSearchStatus, KolSearchStatusEnum } from '@trinity/chat';
import type { ChatState, KolSearchState } from './interfaces';
import { generateEmptyKolSearch } from './state.initial-state';

export function kolSearchesReducer(state: KolSearchState.State, action: ChatState.Action): KolSearchState.State {

  switch (action.type) {
    case 'kol-search-added': {
      return state.concat(action.payload.item);
    }

    case 'kol-search/recommendations-loading': {
      return state.map(ks => {
        if (ks.identifier === action.payload.identifier) {
          return {
            ...ks,
            recommendations: {
              ...ks.recommendations,
              status: 'loading',
            },
          };
        }
        return ks;
      });
    }

    case 'kol-search/recommendations-loaded': {
      return state.map(ks => {
        if (ks.identifier === action.payload.identifier) {
          return {
            ...ks,
            recommendations: {
              status: 'loaded',
              items: action.payload.items,
              total: action.payload.total,
            },
          };
        }
        return ks;
      });
    }

    case 'kol-search/recommendations-added': {
      return state.map(ks => {
        if (ks.identifier === action.payload.identifier) {
          return {
            ...ks,
            recommendations: {
              ...ks.recommendations,
              items: ks.recommendations.items.concat(action.payload.items),
            },
          };
        }
        return ks;
      });
    }

    case 'kol-search-form-begin': {
      return state.map(kolSearch => {
        if (kolSearch.identifier === action.payload.identifier) {
          return {
            ...kolSearch,
            status: KolSearchStatusEnum.FormEntry,
            form: {
              name: '',
            },
          };
        }
        return kolSearch;
      });
    }

    case 'kol-search-form-updated':
      return state.map(kolSearch => {
        if (kolSearch.identifier === action.payload.identifier) {
          return {
            ...kolSearch,
            form: {
              ...kolSearch.form,
              ...action.payload.form,
            },
          };
        }
        return kolSearch;
      });

    case 'kol-search-submitted':
      return state.map(kolSearch => {
        if (kolSearch.identifier === action.payload.identifier) {
          return {
            ...kolSearch,
            status: KolSearchStatusEnum.FormSubmitted,
          };
        }
        return kolSearch;
      });

    case 'kol-search-results-available': {
      const results = state.map(kolSearch => {
        if (kolSearch.identifier === action.payload.identifier) {
          return action.payload.result;
        }
        return kolSearch;
      });

      // Only generate empty search with recommendations if this is an initial search
      // (not a filter/sort operation on existing results)
      if (action.payload.result.status === KolSearchStatusEnum.NoResultsReturned) {
        const existingSearch = state.find(ks => ks.identifier === action.payload.identifier);

        if (existingSearch && existingSearch.results === null) {
          return results.concat(generateEmptyKolSearch());
        }
      }

      return results;
    }

    case 'kol-search-cancelled': {
      const existing = state.map<Chat.KolSearch>(kolSearch => {
        if (kolSearch.identifier === action.payload.identifier) {
          return {
            ...kolSearch,
            status: KolSearchStatusEnum.SearchCancelled,
          };
        }
        return kolSearch;
      });
      return existing.concat(generateEmptyKolSearch());
    }

    case 'kol-search-profile-selected':
      return state.map(kolSearch => {
        if (kolSearch.identifier === action.payload.identifier) {
          return {
            ...kolSearch,
            status: KolSearchStatusEnum.ProfileSelectionSubmitted,
          };
        }
        return kolSearch;
      });

    case 'kol-search-profile-available': {
      return state.map(kolSearch => {
        if (kolSearch.identifier === action.payload.identifier) {
          return action.payload.result;
        }
        return kolSearch;
      });
    }

    case 'kol-search-form-submission-errored':
      return state.map(kolSearch => {
        if (kolSearch.identifier === action.payload.identifier) {
          return {
            ...kolSearch,
            status: KolSearchStatusEnum.FormSubmissionErrored,
            errorTraceId: action.payload.traceId,
          };
        }
        return kolSearch;
      });

    case 'kol-search-profile-selection-errored':
      return state.map(kolSearch => {
        if (kolSearch.identifier === action.payload.identifier) {
          return {
            ...kolSearch,
            status: KolSearchStatusEnum.ProfileSelectionErrored,
            errorTraceId: action.payload.traceId,
          };
        }
        return kolSearch;
      });

    case 'kol-search/question-bank-question-selected':
      return [{
        id: null,
        createdOn: new Date(),
        status: KolSearchStatusEnum.FormEntry,
        identifier: createId(),
        followupQuestions: [],
        form: {
          name: '',
        },
        results: null,
        profile: null,
        recommendations: {
          status: 'skipped',
          items: [],
          total: 0,
        },
        citations: [],
      }];

    case 'kol-search/new-search-initiated':
      return state.map(m => {
        if (m.identifier !== action.payload.oldIdentifier) {
          return m;
        }
        return {
          ...m,
          status: KolSearchStatusEnum.SearchCancelled,
        };
      }).concat({
        id: null,
        createdOn: new Date(),
        status: KolSearchStatusEnum.FormEntry,
        identifier: createId(),
        followupQuestions: [],
        form: {
          name: '',
        },
        results: null,
        profile: null,
        recommendations: {
          status: 'skipped',
          items: [],
          total: 0,
        },
        citations: [],
      });

    default:
      return state;
  }

}